/**
 * Calendario mensile degli impegni della squadra: allenamenti (V10) e
 * partite (V16).
 *
 * Una griglia da lunedi a domenica sui mesi dati, con gli impegni nel loro
 * giorno italiano. Su telefono ogni giorno mostra un pallino per impegno,
 * colorato per stato; da tablet in su una pastiglia dello stesso colore.
 * Toccando un giorno se ne leggono le righe nella card del giorno, sotto la
 * griglia o accanto sugli schermi larghi: cosa mostrarvi lo decide la vista.
 */

import type { ReactNode } from 'react';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { grigliaDelMese, nomeMese } from './calendario.js';
import { formatoGiorno, giornoUtc } from './date.js';
import { StatoVuoto } from './StatoVuoto.js';

const INTESTAZIONE = [
  ['L', 'lun'],
  ['M', 'mar'],
  ['M', 'mer'],
  ['G', 'gio'],
  ['V', 'ven'],
  ['S', 'sab'],
  ['D', 'dom'],
] as const;

/** Da tablet in su si leggono due impegni per giorno, poi "+N". */
const IN_CELLA = 2;

const formatoNomeGiorno = formatoGiorno({ weekday: 'long', day: 'numeric', month: 'long' });

const nomeGiorno = (data: string) => formatoNomeGiorno.format(giornoUtc(data));

export function Pallino({ colore, className }: { colore: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2 shrink-0 rounded-full', colore, className)}
    />
  );
}

export function CalendarioMese<T extends { id: string }>({
  mesi,
  mese,
  giorno,
  oggi,
  giorni,
  pallino,
  pastiglia,
  legenda,
  conteggio,
  invito,
  dettaglio,
  onMese,
  onGiorno,
}: {
  /** I mesi che si possono sfogliare, in ordine. */
  mesi: readonly string[];
  mese: string;
  /** Il giorno scelto nell'URL; se non e del mese, vale oggi quando c'e. */
  giorno: string | null;
  oggi: string;
  /** Gli impegni mostrati, per giorno italiano e in ordine di ora. */
  giorni: ReadonlyMap<string, readonly T[]>;
  /** Il fondo del pallino su telefono. */
  pallino: (t: T) => string;
  /** La pastiglia da tablet in su: il testo e i colori. */
  pastiglia: (t: T) => { testo: ReactNode; className: string };
  legenda: readonly { etichetta: string; colore: string }[];
  /** "1 allenamento", "3 partite", per chi legge con lo screen reader. */
  conteggio: (n: number) => string;
  /** Cosa dice la card del giorno finche non se ne sceglie uno. */
  invito: string;
  /** La card del giorno scelto: una nota sotto il titolo e il contenuto. */
  dettaglio: (giorno: string, impegni: readonly T[]) => { nota?: ReactNode; contenuto: ReactNode };
  onMese: (mese: string) => void;
  onGiorno: (giorno: string) => void;
}) {
  const i = mesi.indexOf(mese);
  const scelto = giorno?.startsWith(mese) ? giorno : oggi.startsWith(mese) ? oggi : null;
  const delGiorno = scelto ? dettaglio(scelto, giorni.get(scelto) ?? []) : null;

  return (
    <div className="grid items-start gap-4 @5xl:grid-cols-3">
      <Card className="@5xl:col-span-2">
        <CardHeader className="flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="icon"
            aria-label="Mese precedente"
            disabled={i <= 0}
            onClick={() => onMese(mesi[i - 1]!)}
          >
            <ChevronLeftIcon />
          </Button>
          <div className="flex items-center gap-2">
            <CalendarDaysIcon aria-hidden className="size-5 text-muted-foreground" />
            <CardTitle className="text-lg font-semibold first-letter:uppercase" aria-live="polite">
              <h2>{nomeMese(mese)}</h2>
            </CardTitle>
            {mesi.includes(oggi.slice(0, 7)) && !oggi.startsWith(mese) && (
              <Button variant="ghost" size="sm" onClick={() => onMese(oggi.slice(0, 7))}>
                Oggi
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Mese successivo"
            disabled={i >= mesi.length - 1}
            onClick={() => onMese(mesi[i + 1]!)}
          >
            <ChevronRightIcon />
          </Button>
        </CardHeader>

        <CardContent className="flex flex-col gap-3 px-2 @xl:px-(--card-spacing)">
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="grid grid-cols-7 bg-muted" aria-hidden>
              {INTESTAZIONE.map(([breve, lungo], n) => (
                <div
                  key={n}
                  className={cn(
                    'py-1.5 text-center text-[13px] font-semibold text-muted-foreground uppercase',
                    n > 0 && 'border-l border-border',
                  )}
                >
                  <span className="@xl:hidden">{breve}</span>
                  <span className="hidden @xl:inline">{lungo}</span>
                </div>
              ))}
            </div>
            {grigliaDelMese(mese).map((settimana) => (
              <div key={settimana[0]!.data} className="grid grid-cols-7 border-t border-border">
                {settimana.map(({ data, delMese }, n) => {
                  const numero = Number(data.slice(8));
                  const bordo = n > 0 && 'border-l border-border';
                  if (!delMese) {
                    return (
                      <div
                        key={data}
                        aria-hidden
                        className={cn(
                          'min-h-14 bg-muted/50 p-1 text-center text-sm text-muted-foreground/50 @xl:min-h-20 @xl:text-left',
                          bordo,
                        )}
                      >
                        {numero}
                      </div>
                    );
                  }
                  const elenco = giorni.get(data) ?? [];
                  return (
                    <button
                      key={data}
                      type="button"
                      aria-pressed={data === scelto}
                      aria-label={`${nomeGiorno(data)}${data === oggi ? ', oggi' : ''}: ${conteggio(
                        elenco.length,
                      )}`}
                      onClick={() => onGiorno(data)}
                      className={cn(
                        'flex min-h-14 min-w-0 flex-col items-center gap-1 p-1 hover:bg-accent @xl:min-h-20 @xl:items-stretch',
                        bordo,
                        data === scelto && 'bg-accent inset-ring-2 inset-ring-foreground',
                      )}
                    >
                      <span
                        className={cn(
                          'grid size-7 place-items-center rounded-full text-sm tabular-nums',
                          data === oggi && 'bg-primary font-semibold text-primary-foreground',
                        )}
                      >
                        {numero}
                      </span>
                      {elenco.length > 0 && (
                        <span className="flex flex-wrap justify-center gap-0.5 @xl:hidden">
                          {elenco.slice(0, 3).map((t) => (
                            <Pallino key={t.id} colore={pallino(t)} />
                          ))}
                          {elenco.length > 3 && <span className="text-[10px] leading-2">+</span>}
                        </span>
                      )}
                      <span className="hidden min-w-0 flex-col gap-0.5 @xl:flex">
                        {elenco.slice(0, IN_CELLA).map((t) => {
                          const { testo, className } = pastiglia(t);
                          return (
                            <span
                              key={t.id}
                              className={cn(
                                'truncate rounded-sm px-1 text-xs font-semibold tabular-nums',
                                className,
                              )}
                            >
                              {testo}
                            </span>
                          );
                        })}
                        {elenco.length > IN_CELLA && (
                          <span className="text-xs text-muted-foreground">
                            +{elenco.length - IN_CELLA}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
            {legenda.map((v) => (
              <li key={v.etichetta} className="flex items-center gap-1.5">
                <Pallino colore={v.colore} />
                {v.etichetta}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card className="bg-muted">
        {scelto && delGiorno ? (
          <>
            <CardHeader>
              <CardTitle className="font-semibold first-letter:uppercase">
                <h2>{nomeGiorno(scelto)}</h2>
              </CardTitle>
              {delGiorno.nota && <CardDescription>{delGiorno.nota}</CardDescription>}
            </CardHeader>
            <CardContent className="flex flex-col gap-3 px-2 @xl:px-(--card-spacing)">
              {delGiorno.contenuto}
            </CardContent>
          </>
        ) : (
          <CardContent>
            <StatoVuoto titolo="Nessun giorno scelto" descrizione={invito} />
          </CardContent>
        )}
      </Card>
    </div>
  );
}
